import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { COUNTRIES } from "../lib/countries";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "../components/SEO";

// Small inline component: the legal-acceptance checkbox shown above each
// auth form's submit button. Renders kept-clickable links to Terms,
// Privacy and Contact (all 3 publicly accessible from /terms /privacy
// /contact without an account) so a visitor can read them BEFORE they
// commit. The form's submit button is disabled until this box is ticked.
function LegalConsent({ accepted, onChange, testid, mode }) {
    return (
        <label
            className="flex items-start gap-2.5 text-sm leading-relaxed select-none cursor-pointer"
            data-testid={testid}
        >
            <Checkbox
                checked={accepted}
                onCheckedChange={(v) => onChange(!!v)}
                className="mt-0.5 shrink-0"
                data-testid={`${testid}-checkbox`}
            />
            <span className="text-foreground/80">
                {mode === "register"
                    ? "By creating an account I agree to the "
                    : "I have read and agree to the "}
                <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6C63FF] font-semibold hover:underline"
                    data-testid={`${testid}-terms-link`}
                >
                    Terms &amp; Conditions
                </Link>{" and "}
                <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#6C63FF] font-semibold hover:underline"
                    data-testid={`${testid}-privacy-link`}
                >
                    Privacy Policy
                </Link>{"."}
            </span>
        </label>
    );
}

export default function Auth() {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    // After auth always land on Home — explicitly NOT honouring
    // `location.state.from`, because users who tap "Ideas" in a marketing
    // email or hit a deep URL prior to login expected to verify they
    // exist, not to be deep-linked past their first home-tab experience.
    const redirect = "/";
    const [tab, setTab] = useState("login");

    // Legal acceptance — required to enable submit on either form. Tracked
    // separately for login and register so toggling the tabs doesn't leak
    // a single accept state across both.
    const [loginAccepted, setLoginAccepted] = useState(false);
    const [regAccepted, setRegAccepted] = useState(false);

    const [loginEmail, setLoginEmail] = useState("");
    const [loginPw, setLoginPw] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);

    const [name, setName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regPw, setRegPw] = useState("");
    const [country, setCountry] = useState("");
    const [regErr, setRegErr] = useState("");
    const [regLoading, setRegLoading] = useState(false);

    const onLogin = async (e) => {
        e.preventDefault();
        if (!loginAccepted) {
            setLoginErr("Please accept the Terms & Privacy to continue.");
            return;
        }
        setLoginErr(""); setLoginLoading(true);
        const r = await login(loginEmail, loginPw);
        setLoginLoading(false);
        if (r.ok) { toast.success("Welcome back!"); navigate(redirect, { replace: true }); }
        else {
            setLoginErr(r.error || "Login failed");
            if ((r.error || "").toLowerCase().includes("too many")) toast.error(r.error);
        }
    };

    const onRegister = async (e) => {
        e.preventDefault();
        if (!regAccepted) {
            setRegErr("Please accept the Terms & Privacy to continue.");
            return;
        }
        if (!country) { setRegErr("Please select your country"); return; }
        setRegErr(""); setRegLoading(true);
        const r = await register({ name, email: regEmail, password: regPw, country });
        setRegLoading(false);
        if (r.ok) { toast.success("Account created!"); navigate(redirect, { replace: true }); }
        else setRegErr(r.error || "Registration failed");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <SEO
                title="Sign in or create an account"
                description="Join Ideas Mine to discover unsolved problems, post your own startup ideas, chat with our AI Idea Studio and connect with founders worldwide. Free to start."
                path="/auth"
                noindex={false}
            />
            {/* Left brand panel */}
            <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#050510] text-white overflow-hidden">
                <div className="absolute inset-0 opacity-30" style={{
                    background: "radial-gradient(800px circle at 20% 30%, rgba(108,99,255,0.4), transparent 50%), radial-gradient(700px circle at 80% 80%, rgba(255,138,0,0.25), transparent 50%)",
                }} />
                <div className="relative flex items-center gap-3">
                    <img
                        src="/logo-512.png"
                        alt="Ideas Mine"
                        className="h-14 w-14 rounded-xl object-cover"
                        data-testid="auth-brand-logo"
                    />
                    <div className="font-display font-black text-4xl tracking-tight leading-none">
                        <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                    </div>
                </div>
                <div className="relative">
                    <p className="font-display font-black italic text-4xl xl:text-5xl tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#FF8A00] via-[#FFB347] to-[#6C63FF]">
                        Igniting Innovative Solutions.
                    </p>
                    <h1 className="font-display font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter mb-8">
                        Mine the next<br />
                        billion-dollar<br />
                        <span className="text-[#6C63FF]">startup idea.</span>
                    </h1>
                    <p className="text-white/70 max-w-md leading-relaxed text-lg">
                        Discover real-world problems waiting to be solved. Post your own
                        ideas. Validate with AI. Sell what you create.
                    </p>
                </div>
                <div className="relative text-xs uppercase tracking-[0.2em] text-white/40">
                    © {new Date().getFullYear()} · A community for entrepreneurs
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md fade-in">
                    <div className="lg:hidden mb-8 flex items-center gap-2">
                        <img
                            src="/logo-192.png"
                            alt="Ideas Mine"
                            className="h-10 w-10 rounded-lg object-cover"
                            data-testid="auth-mobile-logo"
                        />
                        <div className="font-display font-black text-xl tracking-tight">
                            <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                        </div>
                    </div>

                    <Tabs value={tab} onValueChange={setTab}>
                        <TabsList className="grid grid-cols-2 mb-8" data-testid="auth-tabs">
                            <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
                            <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
                        </TabsList>

                        <TabsContent value="login">
                            <h2 className="font-display font-black text-3xl mb-2 tracking-tight brand-text">Welcome back</h2>
                            <p className="text-muted-foreground mb-6 text-sm">Sign in to continue mining ideas.</p>
                            <form onSubmit={onLogin} className="space-y-4" data-testid="login-form">
                                <div>
                                    <Label htmlFor="login-email">Email</Label>
                                    <Input id="login-email" type="email" required
                                        value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                                        placeholder="you@email.com"
                                        data-testid="login-email" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="login-password">Password</Label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs text-[#6C63FF] font-semibold hover:underline"
                                            data-testid="forgot-password-link"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <Input id="login-password" type="password" required
                                        value={loginPw} onChange={(e) => setLoginPw(e.target.value)}
                                        placeholder="••••••••"
                                        data-testid="login-password" />
                                </div>
                                {loginErr && (
                                    <div className="text-sm text-red-500" data-testid="login-error">{loginErr}</div>
                                )}
                                <LegalConsent
                                    accepted={loginAccepted}
                                    onChange={setLoginAccepted}
                                    testid="login-legal-consent"
                                    mode="login"
                                />
                                <Button type="submit" disabled={loginLoading || !loginAccepted}
                                    className="w-full brand-button" data-testid="login-submit">
                                    {loginLoading ? "Signing in…" : "Sign in"} <ArrowRight size={16} className="ml-1" />
                                </Button>
                            </form>
                        </TabsContent>

                        <TabsContent value="register">
                            <h2 className="font-display font-black text-3xl mb-2 tracking-tight brand-text">Join Ideas Mine</h2>
                            <p className="text-muted-foreground mb-6 text-sm">Pick a country to personalize your feed.</p>
                            <form onSubmit={onRegister} className="space-y-4" data-testid="register-form">
                                <div>
                                    <Label htmlFor="reg-name">Full name</Label>
                                    <Input id="reg-name" required value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Jane Doe" data-testid="register-name" />
                                </div>
                                <div>
                                    <Label htmlFor="reg-email">Email</Label>
                                    <Input id="reg-email" type="email" required value={regEmail}
                                        onChange={(e) => setRegEmail(e.target.value)}
                                        placeholder="you@email.com" data-testid="register-email" />
                                </div>
                                <div>
                                    <Label htmlFor="reg-pw">Password</Label>
                                    <Input id="reg-pw" type="password" required minLength={6}
                                        value={regPw} onChange={(e) => setRegPw(e.target.value)}
                                        placeholder="At least 6 characters"
                                        data-testid="register-password" />
                                </div>
                                <div>
                                    <Label>Country</Label>
                                    <Select value={country} onValueChange={setCountry}>
                                        <SelectTrigger data-testid="register-country">
                                            <SelectValue placeholder="Select your country" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {COUNTRIES.map((c) => (
                                                <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {regErr && (
                                    <div className="text-sm text-red-500" data-testid="register-error">{regErr}</div>
                                )}
                                <LegalConsent
                                    accepted={regAccepted}
                                    onChange={setRegAccepted}
                                    testid="register-legal-consent"
                                    mode="register"
                                />
                                <Button type="submit" disabled={regLoading || !regAccepted}
                                    className="w-full brand-button" data-testid="register-submit">
                                    {regLoading ? "Creating account…" : "Create account"} <ArrowRight size={16} className="ml-1" />
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </div>
    );
}
